import { classNames } from 'primereact/utils';
import { useState } from 'react';

import styles from './CustomIcon.module.css';

type CustomIconProps = {
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  hoverIcon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  iconOptions?: React.SVGProps<SVGSVGElement>;
  className?: string;
  onClick?: () => void;
  showTextOnHover?: boolean;
  hoverText?: string;
  hoverTextColor?: string;
  /**
   * Side of the icon the hover text is rendered on. Use `left` when the icon
   * sits at the end of a container, so the text does not overflow it.
   */
  hoverTextPosition?: 'left' | 'right';
  /** Dims the icon and blocks interaction, for example while data is loading. */
  disabled?: boolean;
};

const CustomIcon = ({
  icon,
  hoverIcon,
  iconOptions,
  className,
  onClick,
  showTextOnHover = false,
  hoverText,
  hoverTextColor,
  hoverTextPosition = 'right',
  disabled = false,
}: CustomIconProps): JSX.Element => {
  const Icon = icon;
  const HoverIcon = hoverIcon;
  const [isHovered, setIsHovered] = useState<boolean>(false);

  const onMouseEnter = (): void => {
    setIsHovered(true);
  };

  const onMouseLeave = (): void => {
    setIsHovered(false);
  };

  return (
    <div className='relative inline-flex flex-row align-items-center'>
      <i
        /* pointer-events suppresses the hover state too, which a dimmed icon
           should not show */
        className={classNames(className, { 'opacity-50 pointer-events-none': disabled })}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        role={onClick && !disabled ? 'button' : undefined}
        onClick={disabled ? undefined : onClick}
        aria-disabled={disabled}
      >
        {isHovered && HoverIcon ? <HoverIcon {...iconOptions} /> : <Icon {...iconOptions} />}
      </i>
      {isHovered && showTextOnHover && (
        <span
          className={classNames(
            styles['hover-text'],
            styles[`hover-text-${hoverTextPosition}`],
            `text-${hoverTextColor}`,
          )}
        >
          {hoverText}
        </span>
      )}
    </div>
  );
};

export default CustomIcon;
